import { ConversationMessageSchema } from '@quromedical/communication';
import { z } from 'zod';
import { DisplayReferenceSchema, UUIDSchema } from '../domain/models';
import { PaginationSchema } from '../domain/pagination';
import { DateTimeSchema, PaginatedBundleSchema } from '../domain/schema';
import { ContentTypeSchema } from '../media/base';

export enum CommunicationCategoryCode {
  Notification = 'communication.category.notification',
  Incoming = 'communication.category.incoming',
}

export enum CommunicationTopicCode {
  OperationsWelcomeTCP = 'communication.operation.welcome.tcp',
  OperationsVitalCollectionTCP = 'communication.operation.welcome.tcp',
  OperationsCollectionTCP = 'communication.operation.collection.tcp',
  OperationsCollectionDischargeTCP = 'communication.operation.collection.discharge',
  FreeChat = 'communication.topic.free.chat',
}

export enum CommunicationChannelCode {
  Email = 'communication.channel.email',
  SMS = 'communication.channel.sms',
  WhatsApp = 'communication.channel.whatsapp',
}

export const CommunicationChannelCodeDescription: Record<
  CommunicationChannelCode,
  string
> = {
  [CommunicationChannelCode.WhatsApp]: 'WhatsApp',
  [CommunicationChannelCode.Email]: 'Email',
  [CommunicationChannelCode.SMS]: 'SMS',
};

const communicationStatus = [
  'preparation',
  'in-progress',
  'not-done',
  'on-hold',
  'stopped',
  'completed',
  'entered-in-error',
  'unknown',
] as const;

const communicationPriority = ['routine', 'urgent', 'asap', 'stat'] as const;

const CommunicationStatusSchema = z.enum(communicationStatus);
const CommunicationPrioritySchema = z.enum(communicationPriority);
const CommunicationCategorySchema = z.nativeEnum(CommunicationCategoryCode);
const CommunicationChannelSchema = z
  .array(z.nativeEnum(CommunicationChannelCode))
  .min(1);
const CommunicationTopicSchema = z.nativeEnum(CommunicationTopicCode);
const AttachmentSchema = z.object({
  data: z.string(),
  contentType: ContentTypeSchema,
});

export const communicationContentSchema = z.object({
  content: z.string(),
  attachment: DisplayReferenceSchema.optional(),
});

export const communicationContentCreateSchema = z.object({
  content: z.string(),
  attachmentData: AttachmentSchema.optional(),
});

export const conversationContentSchema = z.object({
  content: z.string(),
});

export const CommunicationSchema = z.object({
  inResponseTo: DisplayReferenceSchema.optional(),
  status: CommunicationStatusSchema,
  category: CommunicationCategorySchema,
  priority: CommunicationPrioritySchema,
  channel: CommunicationChannelSchema,
  patient: DisplayReferenceSchema,
  topic: CommunicationTopicSchema,
  admission: DisplayReferenceSchema,
  sent: DateTimeSchema,
  received: DateTimeSchema.optional(),
  recipient: DisplayReferenceSchema,
  sender: DisplayReferenceSchema,
  content: z.array(communicationContentSchema).min(1),
});

export const CommunicationGetResponseSchema = CommunicationSchema.partial();

export const ConversationCreateRequestSchema = z.object({
  patientId: UUIDSchema,
});

export const ConversationGetRequestSchema = ConversationCreateRequestSchema;

export const ConversationGetResponseSchema = z.object({
  id: z.string(),
});

export const CommunicationGetAllResponseSchema = PaginatedBundleSchema.extend({
  conversations: ConversationMessageSchema.array(),
});

export const CommunicationCreateRequestSchema = z.object({
  inResponseTo: DisplayReferenceSchema.optional(),
  status: CommunicationStatusSchema,
  category: CommunicationCategorySchema,
  priority: CommunicationPrioritySchema,
  channel: CommunicationChannelSchema,
  patient: DisplayReferenceSchema,
  topic: CommunicationTopicSchema,
  admission: DisplayReferenceSchema,
  recipient: DisplayReferenceSchema,
  sender: DisplayReferenceSchema,
  content: z.array(communicationContentCreateSchema).min(1),
  messageId: z.string().optional(),
});

export const CommunicationSearchSchema = PaginationSchema.extend({
  subject: UUIDSchema.optional().describe('The subject of the communication'),
  nextOfKin: UUIDSchema.optional().describe('The next of kin of the patient'),
  medium: z.string().optional().describe('A channel of communication'),
  category: z.string().optional().describe('Message category'),
  recipient: UUIDSchema.optional().describe(
    'The recipient of the communication',
  ),
});

export type Communication = z.infer<typeof CommunicationSchema>;
export type CommunicationContent = z.infer<typeof communicationContentSchema>;
export type CommunicationGetResponse = z.infer<
  typeof CommunicationGetResponseSchema
>;
export type CommunicationGetAllResponse = z.infer<
  typeof CommunicationGetAllResponseSchema
>;
export type CommunicationCreateRequest = z.infer<
  typeof CommunicationCreateRequestSchema
>;
export type CommunicationSearch = z.infer<typeof CommunicationSearchSchema>;

export type Conversation = z.infer<typeof ConversationGetResponseSchema>;
export type ConversationContent = z.infer<typeof conversationContentSchema>;
export type ConversationCreateRequest = z.infer<
  typeof ConversationCreateRequestSchema
>;
export type ConversationGetRequest = z.infer<
  typeof ConversationCreateRequestSchema
>;
export type ConversationGetResponse = z.infer<
  typeof ConversationGetResponseSchema
>;
